import type {
  ApiKeyCreateRequest,
  ApiKeyCreateResponse,
  ApiKeyListConstraints,
  ApiKeyResponse,
  ApiKeyUpdateRequest,
  ApiKeyRevokeResponse,
} from '../types';
import type { RequestFn } from '../client';

/** Create and manage API keys for a merchant account. */
export class ApiKeys {
  constructor(private readonly request: RequestFn) {}

  /**
   * Create a new API key for a merchant.
   *
   * @param merchantId - The merchant account ID.
   * @param params - Key creation parameters (name, expiry, etc.).
   * @returns The newly created key including the plaintext secret (shown once only).
   *
   * @example
   * ```typescript
   * const { key_value } = await delopay.apiKeys.create('merch_123', { name: 'Production key' });
   * ```
   */
  async create(merchantId: string, params: ApiKeyCreateRequest): Promise<ApiKeyCreateResponse> {
    return this.request('POST', `/api-keys/${encodeURIComponent(merchantId)}`, { body: params });
  }

  /**
   * Retrieve metadata about an API key (does not return the plaintext secret).
   *
   * @param merchantId - The merchant account ID.
   * @param keyId - The API key ID.
   * @returns The API key metadata.
   */
  async retrieve(merchantId: string, keyId: string): Promise<ApiKeyResponse> {
    return this.request(
      'GET',
      `/api-keys/${encodeURIComponent(merchantId)}/${encodeURIComponent(keyId)}`,
    );
  }

  /**
   * Update an API key's name or expiry.
   *
   * @param merchantId - The merchant account ID.
   * @param keyId - The API key ID to update.
   * @param params - Fields to update.
   * @returns The updated API key metadata.
   */
  async update(
    merchantId: string,
    keyId: string,
    params: ApiKeyUpdateRequest,
  ): Promise<ApiKeyResponse> {
    return this.request(
      'POST',
      `/api-keys/${encodeURIComponent(merchantId)}/${encodeURIComponent(keyId)}`,
      { body: params },
    );
  }

  /**
   * Revoke an API key, immediately invalidating it.
   *
   * @param merchantId - The merchant account ID.
   * @param keyId - The API key ID to revoke.
   * @returns Revocation confirmation.
   */
  async revoke(merchantId: string, keyId: string): Promise<ApiKeyRevokeResponse> {
    return this.request(
      'DELETE',
      `/api-keys/${encodeURIComponent(merchantId)}/${encodeURIComponent(keyId)}`,
    );
  }

  /**
   * List all API keys for a merchant.
   *
   * @param merchantId - The merchant account ID.
   * @returns Array of API key metadata objects.
   */
  async list(merchantId: string): Promise<ApiKeyResponse[]> {
    return this.request('GET', `/api-keys/${encodeURIComponent(merchantId)}/list`);
  }

  // --- Profile-scoped (shop-level) API keys ---------------------------------
  //
  // JWT-authenticated routes under `/account/{merchantId}/profile/api-keys`.
  // The caller's shop (business profile) comes from the JWT, never from the
  // request, so a shop-scoped user can only mint/list/manage keys pinned to
  // their own shop. Requires a backend with profile-scoped API key support.

  /**
   * Create a new API key pinned to the caller's shop (business profile).
   * `POST /account/{merchantId}/profile/api-keys`
   *
   * @param merchantId - The merchant account ID.
   * @param params - Key creation parameters (name, expiry, etc.).
   * @returns The newly created key including the plaintext secret (shown once
   *   only) and the `profile_id` it is pinned to.
   *
   * @example
   * ```typescript
   * const { api_key } = await delopay.apiKeys.createByProfile('merch_123', {
   *   name: 'Shop key',
   *   expiration: 'never',
   * });
   * ```
   */
  async createByProfile(
    merchantId: string,
    params: ApiKeyCreateRequest,
  ): Promise<ApiKeyCreateResponse> {
    return this.request('POST', `/account/${encodeURIComponent(merchantId)}/profile/api-keys`, {
      body: params,
    });
  }

  /**
   * List API keys pinned to the caller's shop (business profile) only.
   * `GET /account/{merchantId}/profile/api-keys`
   *
   * @param merchantId - The merchant account ID.
   * @param params - Optional pagination constraints (`limit`, `skip`).
   * @returns Array of API key metadata objects belonging to the caller's shop.
   */
  async listByProfile(
    merchantId: string,
    params?: ApiKeyListConstraints,
  ): Promise<ApiKeyResponse[]> {
    return this.request('GET', `/account/${encodeURIComponent(merchantId)}/profile/api-keys`, {
      query: params as Record<string, number | null | undefined>,
    });
  }

  /**
   * Retrieve metadata about a shop-pinned API key (does not return the
   * plaintext secret). `GET /account/{merchantId}/profile/api-keys/{keyId}`
   *
   * @param merchantId - The merchant account ID.
   * @param keyId - The API key ID.
   * @returns The API key metadata.
   */
  async retrieveByProfile(merchantId: string, keyId: string): Promise<ApiKeyResponse> {
    return this.request(
      'GET',
      `/account/${encodeURIComponent(merchantId)}/profile/api-keys/${encodeURIComponent(keyId)}`,
    );
  }

  /**
   * Update a shop-pinned API key's name, description, or expiry.
   * `POST /account/{merchantId}/profile/api-keys/{keyId}`
   *
   * @param merchantId - The merchant account ID.
   * @param keyId - The API key ID to update.
   * @param params - Fields to update.
   * @returns The updated API key metadata.
   */
  async updateByProfile(
    merchantId: string,
    keyId: string,
    params: ApiKeyUpdateRequest,
  ): Promise<ApiKeyResponse> {
    return this.request(
      'POST',
      `/account/${encodeURIComponent(merchantId)}/profile/api-keys/${encodeURIComponent(keyId)}`,
      { body: params },
    );
  }

  /**
   * Revoke a shop-pinned API key, immediately invalidating it.
   * `DELETE /account/{merchantId}/profile/api-keys/{keyId}`
   *
   * @param merchantId - The merchant account ID.
   * @param keyId - The API key ID to revoke.
   * @returns Revocation confirmation.
   */
  async revokeByProfile(merchantId: string, keyId: string): Promise<ApiKeyRevokeResponse> {
    return this.request(
      'DELETE',
      `/account/${encodeURIComponent(merchantId)}/profile/api-keys/${encodeURIComponent(keyId)}`,
    );
  }
}
